import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Paper,
  Stack,
  Button,
  Divider,
  Card,
  CardContent,
  Chip,
  Alert,
  AlertTitle,
  Select,
  MenuItem,
  TextField,
  IconButton,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  FirstPage,
  LastPage,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import {
  getSendBlockReason,
  isProductSelectionDisabled,
  isProductSendable,
} from "../../../../utils/sendProductBulkFilters";

const DEFAULT_MAX_SELECTED = 500;

const ProductSelectionStep = ({
  products,
  onBack,
  onComplete,
  totalCount,
  setPage,
  page,
  setPageSize,
  pageSize,
  getCategoryProducts,
  selectedProducts,
  setSelectedProducts,
  maxSelectedProducts = DEFAULT_MAX_SELECTED,
  showPageSizeSelector = true,
  onSelectAllListed,
  selectingAllListed = false,
  selectAllProgress = null,
}) => {
  const [selectAll, setSelectAll] = useState();
  const [newPage, setNewPage] = useState(0);
  const [jumpToPageInput, setJumpToPageInput] = useState("");
  const productsPerPage = pageSize;
  const pageCount = Math.max(1, Math.ceil(totalCount / productsPerPage) || 1);
  const showPager = totalCount > productsPerPage;
  const displayedProducts = products.slice(0, productsPerPage);
  const rangeStart =
    totalCount === 0 ? 0 : (page - 1) * productsPerPage + 1;
  const rangeEnd = Math.min(page * productsPerPage, totalCount);

  const handleDisplayedProducts = () => {
    return displayedProducts.filter(isProductSendable).map((p) => p.id);
  };
  let allDisplayedProductIds = handleDisplayedProducts();

  const handleProductChange = (productId, checked) => {
    setSelectedProducts((prev) => {
      if (!checked) return prev.filter((id) => id !== productId);
      if (prev.includes(productId) || prev.length >= maxSelectedProducts) {
        return prev;
      }
      return [...prev, productId];
    });
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    setSelectedProducts((prev) => {
      const otherPageSelections = prev.filter(
        (id) => !allDisplayedProductIds.includes(id)
      );
      if (!checked) return otherPageSelections;
      const merged = [...otherPageSelections];
      for (const id of allDisplayedProductIds) {
        if (merged.length >= maxSelectedProducts) break;
        if (!merged.includes(id)) merged.push(id);
      }
      return merged;
    });
  };

  const goToPage = (targetPage) => {
    const next = Math.max(1, Math.min(pageCount, targetPage));
    setPage(next);
    setNewPage(next);
    setJumpToPageInput(String(next));
  };

  const commitJumpInput = () => {
    const parsed = parseInt(jumpToPageInput, 10);
    if (Number.isFinite(parsed)) {
      goToPage(parsed);
    } else {
      setJumpToPageInput(String(page));
    }
  };

  useEffect(() => {
    setJumpToPageInput(String(page));
  }, [page]);

  const handleItemCheckbox = (product) => isProductSelectionDisabled(product);

  const isAllCurrentPageSelected =
    allDisplayedProductIds.length > 0 &&
    allDisplayedProductIds.every((id) => selectedProducts.includes(id));
  useEffect(() => {
    if (newPage == page) {
      getCategoryProducts();
    }
  }, [page]);
  if (!products || products.length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Alert
          severity="info"
          variant="outlined"
          sx={{
            "& .MuiAlert-message": { width: "100%" },
            mb: 3,
          }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>Ürün Bulunamadı</AlertTitle>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Seçilen kategoride ürününüz bulunamadı.
          </Typography>
          <Button variant="outlined" onClick={onBack} size="small">
            Geri Dön
          </Button>
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: 2,
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Stack spacing={{ xs: 2, sm: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 1, sm: 2 },
          }}
        >
          <Typography
            variant="h5"
            color="primary.main"
            fontWeight="600"
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
            }}
          >
            Ürün Seçimi
          </Typography>

          {selectedProducts.length <= maxSelectedProducts ? (
            <Chip
              label={`${selectedProducts.length} ürün seçildi`}
              color="primary"
              variant="outlined"
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                height: { xs: 24, sm: 32 },
              }}
            />
          ) : (
            <Chip
              label={`${selectedProducts.length} ürün seçildi. Lütfen seçim sayısını ${maxSelectedProducts} ve altı yapınız.`}
              color="warning"
              variant="outlined"
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                height: { xs: 24, sm: 32 },
              }}
            />
          )}
        </Box>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 0.6, fontWeight: 600 }}
          >
            Toplu seçim
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    isAllCurrentPageSelected && displayedProducts.length > 0
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  color="primary"
                  size="small"
                  disabled={selectingAllListed}
                />
              }
              label={
                <Typography variant="body2" fontWeight={500}>
                  Bu sayfadaki tüm ürünleri seç
                </Typography>
              }
              sx={{ m: 0 }}
            />

            {onSelectAllListed ? (
              <>
                <Divider />
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  disabled={selectingAllListed}
                  onClick={onSelectAllListed}
                  sx={{
                    py: 1.1,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Tüm sayfalardaki seçilebilir ürünleri seç
                </Button>
                {selectingAllListed && (
                  <Box>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 0.75 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {selectAllProgress?.statusText ??
                          "Seçilebilir ürünler taranıyor…"}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="primary.main"
                      >
                        %{selectAllProgress?.percent ?? 0}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={selectAllProgress?.percent ?? 0}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                    {(selectAllProgress?.selectedCount ?? 0) > 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.75, display: "block" }}
                      >
                        {selectAllProgress.selectedCount} ürün seçildi
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            ) : null}
          </Stack>
        </Paper>

        <Divider />

        <FormGroup>
          <Stack spacing={{ xs: 1, sm: 2 }}>
            {displayedProducts.map((product) => {
              const selectionDisabled = handleItemCheckbox(product);
              const blockReason = getSendBlockReason(product);

              return (
              <Card
                key={product.id}
                variant="outlined"
                sx={{
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: "grey.50",
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: { xs: 1, sm: 2 },
                    "&:last-child": { pb: { xs: 1, sm: 2 } },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedProducts.includes(product.id)}
                        onChange={(e) =>
                          handleProductChange(product.id, e.target.checked)
                        }
                        disabled={selectionDisabled}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        spacing={{ xs: 1, sm: 2 }}
                        width="100%"
                      >
                        {product.productImages?.length > 0 && (
                          <Box
                            component="img"
                            sx={{
                              width: { xs: 50, sm: 70 },
                              height: { xs: 50, sm: 70 },
                              objectFit: "cover",
                              borderRadius: 1,
                              boxShadow: 1,
                              flexShrink: 0,
                            }}
                            src={product.productImages[0].imageUrl}
                            alt={product.title}
                            referrerPolicy="no-referrer"
                          />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: selectedProducts.includes(product.id)
                                ? 600
                                : 400,
                              color: selectedProducts.includes(product.id)
                                ? "primary.main"
                                : "text.primary",
                              fontSize: { xs: "0.875rem", sm: "1rem" },
                            }}
                          >
                            {product.title}
                          </Typography>

                          {blockReason && (
                            <Typography
                              variant="caption"
                              color="warning.main"
                              sx={{
                                display: "block",
                                mt: 0.75,
                                fontWeight: 600,
                                fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                              }}
                            >
                              {blockReason}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    }
                    sx={{ width: "100%", mr: 0, alignItems: "flex-start" }}
                  />
                </CardContent>
              </Card>
            );
            })}
          </Stack>
        </FormGroup>

        {showPager && (
          <Paper
            variant="outlined"
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              bgcolor: "background.paper",
              borderColor: "divider",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                {showPageSizeSelector ? (
                  <>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      Sayfa başına
                    </Typography>
                    <Select
                      value={pageSize}
                      onChange={(e) => setPageSize(e.target.value)}
                      size="small"
                      sx={{ minWidth: 72, bgcolor: "background.default" }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                      <MenuItem value={500}>500</MenuItem>
                    </Select>
                  </>
                ) : null}
                <Typography variant="body2" color="text.secondary" noWrap>
                  {showPageSizeSelector ? "· " : ""}
                  {rangeStart}–{rangeEnd} / {totalCount} ürün
                </Typography>
              </Stack>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={0.5}
                sx={{ flexShrink: 0 }}
              >
                <IconButton
                  size="small"
                  aria-label="İlk sayfa"
                  disabled={page <= 1}
                  onClick={() => goToPage(1)}
                >
                  <FirstPage fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Önceki sayfa"
                  disabled={page <= 1}
                  onClick={() => goToPage(page - 1)}
                >
                  <ChevronLeft fontSize="small" />
                </IconButton>

                <TextField
                  size="small"
                  value={jumpToPageInput}
                  onChange={(e) => setJumpToPageInput(e.target.value)}
                  onBlur={commitJumpInput}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitJumpInput();
                    }
                  }}
                  inputProps={{
                    min: 1,
                    max: pageCount,
                    style: { textAlign: "center", padding: "6px 0" },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ pl: 0.5 }}
                        >
                          Sayfa
                        </Typography>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="body2" color="text.secondary">
                          / {pageCount}
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: { xs: 140, sm: 160 },
                    mx: 0.5,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "background.default",
                    },
                  }}
                />

                <IconButton
                  size="small"
                  aria-label="Sonraki sayfa"
                  disabled={page >= pageCount}
                  onClick={() => goToPage(page + 1)}
                >
                  <ChevronRight fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Son sayfa"
                  disabled={page >= pageCount}
                  onClick={() => goToPage(pageCount)}
                >
                  <LastPage fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Paper>
        )}

        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
          >
            <Button
              variant="outlined"
              onClick={onBack}
              disabled={selectingAllListed}
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 500,
                maxWidth: { sm: 160 },
              }}
            >
              Geri
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={() => onComplete(selectedProducts)}
              disabled={
                selectingAllListed ||
                selectedProducts.length === 0 ||
                selectedProducts.length > maxSelectedProducts
              }
              fullWidth
              sx={{
                py: 1.15,
                textTransform: "none",
                fontWeight: 600,
                maxWidth: { sm: 280 },
              }}
            >
              Devam ({selectedProducts.length})
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Paper>
  );
};

export default ProductSelectionStep;
